import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { resetPassword } from '../../services/auth';
import { AccentButton } from '../components/Button';
import Input from '../components/Input';
import ThemeSwitch from '../components/ThemeSwitch';
import { handleApiErrorWithMessages } from '../../utils/apiErrorHandler';

const PasswordResetPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!token) {
      setGeneralError('Invalid reset link');
      setTimeout(() => {
        navigate('/password-reset-request');
      }, 2000);
    }
  }, [token, navigate]);

  const onSubmit = async (data) => {
    if (!token) {
      setGeneralError('Invalid reset link');
      return;
    }

    setIsLoading(true);
    setGeneralError(null);
    clearErrors();
    
    try {
      await resetPassword(token, data.newPassword);
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/sign-in');
      }, 2000);
    } catch (error) {
      const { generalError: apiGeneralError, fieldErrors } = handleApiErrorWithMessages(error);
      
      // Set field-specific errors
      Object.keys(fieldErrors).forEach((field) => {
        setError(field, {
          type: 'server',
          message: fieldErrors[field],
        });
      });
      
      // Set general error if present
      if (apiGeneralError) {
        setGeneralError(apiGeneralError);
      } else if (Object.keys(fieldErrors).length === 0) {
        setGeneralError('Failed to reset password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#121212] py-12 px-4 sm:px-6 relative">
      {/* Theme Switch - Top Right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <ThemeSwitch />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0]">
            {isSuccess ? 'Password reset successful!' : 'Set new password'}
          </h1>
          <p className="mt-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
            {isSuccess ? (
              <>
                Your password has been reset successfully. Redirecting to sign in...
                <br />
                <Link
                  to="/sign-in"
                  className="font-medium text-[#121212] dark:text-[#D0D0D0] hover:underline"
                >
                  Go to sign in
                </Link>
              </>
            ) : (
              <>
                Enter your new password below.
                <br />
                <Link
                  to="/sign-in"
                  className="font-medium text-[#121212] dark:text-[#D0D0D0] hover:underline"
                >
                  Back to sign in
                </Link>
              </>
            )}
          </p>
        </div>

        {/* Form */}
        {!isSuccess && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* General Error Display */}
            {generalError && (
              <div className="rounded-xl border border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
                <p className="text-sm text-red-600 dark:text-red-400">{generalError}</p>
              </div>
            )}
            
            <div className="space-y-4">
              {/* New Password Field */}
              <Input
                type="password"
                label="New Password"
                placeholder="Enter new password"
                autoComplete="new-password"
                showPasswordToggle={true}
                error={errors.newPassword?.message}
                {...register('newPassword', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
              />

              {/* Confirm Password Field */}
              <Input
                type="password"
                label="Confirm Password"
                placeholder="Confirm new password"
                autoComplete="new-password"
                showPasswordToggle={true}
                error={errors.confirmPassword?.message}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => {
                    const password = getValues('newPassword');
                    return value === password || 'Passwords do not match';
                  },
                })}
              />
            </div>

            {/* Submit Button */}
            <div>
              <AccentButton
                type="submit"
                variant="primary"
                disabled={isLoading}
                className="w-full h-[48px]"
              >
                {isLoading ? 'Resetting...' : 'Reset password'}
              </AccentButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PasswordResetPage;
